// File: frontend/src/utils/userStatusIconMap.ts
//
// Maps a lifecycle-status marker key (see getNickaltiOwnerMark in
// userStatusUtils.ts) to the react-icons component that represents it.
//
// Kept separate from userStatusUtils.ts for the same reason roleIconMap.ts is
// separate: that file is imported by plain logic and must stay free of icon
// dependencies. The mark helper therefore returns a key, and only the
// components that actually render look the component up here.

import React from "react";
import {
  FaBan,
  FaMoon,
  FaPauseCircle,
  FaSnowflake,
  FaTrashAlt,
  FaUserClock,
  FaUserSlash,
} from "react-icons/fa";

// The component type accepted by react-icons
type IconComponent = React.ComponentType<{ size?: number; className?: string }>;

/**
 * Static map from marker key → react-icons component.
 *
 * The keys are the ones produced by getNickaltiOwnerMark; a status with no
 * marker (ACTIVE, or an unrecognised value) never reaches this map.
 */
export const USER_STATUS_ICON_MAP: Record<string, IconComponent> = {
  // Temporarily restricted by an admin — comes back on its own
  suspended: FaPauseCircle,
  // Permanently restricted by an admin
  banned: FaBan,
  // Paused by the user; lifts itself on their next login
  frozen: FaSnowflake,
  // Self-deletion requested, grace period still running — reversible
  deleting: FaUserClock,
  // Self-deletion carried out; the account is closed for good
  closed: FaUserSlash,
  // Removed by an admin.
  // A trash can rather than another person-shaped glyph: at 12-14px the
  // person-with-a-cross icon this used to be was indistinguishable from the
  // person-with-a-slash used for a self-closed account, which is the one state it
  // most needs to be told apart from.
  removed: FaTrashAlt,
  // No login for a long time
  inactive: FaMoon,
};